import client from './client';

export const tasksApi = {
  // List tasks with filters
  async list(params = {}) {
    const { data } = await client.get('/api/tasks', { params });
    return data;
  },

  // Get single task by key
  async get(key) {
    const { data } = await client.get(`/api/tasks/${key}`);
    return data;
  },

  // Create new task
  async create(task) {
    const { data } = await client.post('/api/tasks', task);
    return data;
  },

  // Update task
  async update(key, updates) {
    const { data } = await client.patch(`/api/tasks/${key}`, updates);
    return data;
  },

  // Bulk update tasks
  async bulkUpdate(updates) {
    const { data } = await client.patch('/api/tasks/bulk', updates);
    return data;
  },

  // Reorder tasks within a status column
  async reorder(status, taskKeys) {
    const { data } = await client.patch('/api/tasks/reorder', { status, taskKeys });
    return data;
  },

  // Add comment to task (supports optional file attachments)
  async addComment(key, body, files = []) {
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('body', body);
      for (const file of files) {
        formData.append('files', file);
      }
      const { data } = await client.post(`/api/tasks/${key}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    }
    const { data } = await client.post(`/api/tasks/${key}/comments`, { body });
    return data;
  },

  // Upload attachment
  async uploadAttachment(key, file) {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await client.post(`/api/tasks/${key}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // Delete attachment
  async deleteAttachment(key, attachmentId) {
    const { data } = await client.delete(`/api/tasks/${key}/attachments/${attachmentId}`);
    return data;
  },

  // Add a Slack channel to a task
  async addSlackChannel(key, url) {
    const { data } = await client.post(`/api/tasks/${key}/slack-channels`, { url });
    return data;
  },

  // Remove a Slack channel from a task
  async removeSlackChannel(key, url) {
    const { data } = await client.delete(`/api/tasks/${key}/slack-channels`, { data: { url } });
    return data;
  },

  // Get all links for a task
  async getLinks(key) {
    const { data } = await client.get(`/api/tasks/${key}/links`);
    return data;
  },

  // Create a link from this task to another
  async createLink(key, targetKey, linkType) {
    const { data } = await client.post(`/api/tasks/${key}/links`, { targetKey, linkType });
    return data;
  },

  // Delete a link
  async deleteLink(key, linkId) {
    const { data } = await client.delete(`/api/tasks/${key}/links/${linkId}`);
    return data;
  },

  // Get link counts for multiple tasks (for board/backlog indicators)
  async getLinkCounts(keys) {
    if (!keys || keys.length === 0) return { counts: {} };
    const { data } = await client.get('/api/tasks/link-counts', {
      params: { keys: keys.join(',') },
    });
    return data;
  },

  // Get ticket links for a task
  async getTicketLinks(key) {
    const { data } = await client.get(`/api/tasks/${key}/ticket-links`);
    return data;
  },

  // Create a ticket link for a task
  async createTicketLink(key, ticketId) {
    const { data } = await client.post(`/api/tasks/${key}/ticket-links`, { ticketId });
    return data;
  },

  // Delete a ticket link from a task
  async deleteTicketLink(key, linkId) {
    const { data } = await client.delete(`/api/tasks/${key}/ticket-links/${linkId}`);
    return data;
  },

  // Get conversation links for a task
  async getConversationLinks(key) {
    const { data } = await client.get(`/api/tasks/${key}/conversation-links`);
    return data;
  },

  // Create a conversation link for a task
  async createConversationLink(key, conversationId) {
    const { data } = await client.post(`/api/tasks/${key}/conversation-links`, { conversationId });
    return data;
  },

  // Delete a conversation link from a task
  async deleteConversationLink(key, linkId) {
    const { data } = await client.delete(`/api/tasks/${key}/conversation-links/${linkId}`);
    return data;
  },
};
